import React, { useState, useEffect } from 'react';
import Avatar from '@mui/material/Avatar';
import { Stack } from '@mui/system';
import { Typography } from '@mui/material';
import { getToken } from '../../auth/auth.js';

/**
 * Creates initials from a display name.
 * @param {string} name The user's full display name.
 * @returns {string} The initials.
 */
function getInitials(name = '') {
  if (!name) return '?'
  const parts = name.split(' ').filter(part => !part.startsWith('['))
  if (parts.length < 2) return ''
  const firstNameInitial = parts[0][0]
  const lastNameInitial = parts[parts.length - 1][0]
  return `${firstNameInitial}${lastNameInitial}`.toUpperCase()
}


// --- Main Component ---

/**
 * Displays a user's contact card with an avatar that falls back to initials.
 * Fetches data from Microsoft Graph API using a provided access token.
 * @param {{profileEmail: string, profileName: string, profileJobTitle: string, avatarLeftRight: string, avatarHeight: number, avatarWidth: number, hideTitle: boolean}} props
 */
function ProfileAvatarCard({ profileEmail, profileName, profileJobTitle, avatarLeftRight='left', avatarHeight=48, avatarWidth=48, hideTitle=false }) {
  const [userData, setUserData] = useState(null);
  const [avatarSrc, setAvatarSrc] = useState(null);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Reset state when the email prop changes
    setUserData(null);
    setAvatarSrc(null);
    setError(null);
    setIsLoading(true);

    const fetchGraphData = async () => {
      if (!profileEmail) {
        setError('No user email provided.');
        setIsLoading(false);
        return;
      }

      try {
        const accessToken = (await getToken()).accessToken;
        if (!accessToken) {
          throw new Error('Could not retrieve access token.');
        }

        const headers = { Authorization: `Bearer ${accessToken}` };
        
        // Define endpoints
        const dataEndpoint = `https://graph.microsoft.com/v1.0/users/${profileEmail}?$select=displayName,jobTitle,mail,department`;
        const photoEndpoint = `https://graph.microsoft.com/v1.0/users/${profileEmail}/photo/$value`;

        // Fetch user data and photo in parallel for efficiency
        const [dataResponse, photoResponse] = await Promise.all([
            fetch(dataEndpoint, { headers }),
            fetch(photoEndpoint, { headers })
        ]);

        // Process user data
        if (!dataResponse.ok) {
            throw new Error(`Failed to fetch user data: ${dataResponse.statusText}`);
        }
        const fetchedUserData = await dataResponse.json();
        setUserData(fetchedUserData);

        // Process user photo (a 404 is an expected, non-critical error)
        if (photoResponse.ok) {
          const imageBlob = await photoResponse.blob();
          setAvatarSrc(URL.createObjectURL(imageBlob));
        }

      } catch (err) {
        console.error(err);
        setError(err?.message || 'Could not load profile.');
      } finally {
        setIsLoading(false);
      }
    };

    fetchGraphData();
  }, [profileEmail]);

  if (isLoading) {
    return <div>Loading profile...</div>;
  }

  if (error) {
    return <div>{error}</div>
  }

  return (
    <span >
      <Stack direction={avatarLeftRight === 'left' ? 'row' : 'row-reverse'} sx={{display: 'inline-flex', alignItems: 'center', fontFamily: 'sans-serif'}} gap='16px'>
      <Avatar
        src={avatarSrc}
        sx={{
          width: avatarWidth,
          height: avatarHeight,
          fontSize: '1.0em',
          bgcolor: '#222d32', //stringToColor(userData?.displayName ?? profileName),
          border: '1px solid #fff'
        }}
      >
        {getInitials(userData?.displayName ?? profileName)}
      </Avatar>
      <Stack rowGap={.5} padding='1%'>
        <Typography variant="body" whiteSpace='nowrap'>
          {userData?.displayName ?? profileName}
        </Typography>
        { (!hideTitle) && (userData?.jobTitle || profileJobTitle) && 
          <Typography variant="caption" fontStyle='text.secondary'>
            { userData?.jobTitle ?? profileJobTitle }
          </Typography>
        }
      </Stack>
      </Stack>
    </span>
  );
}

export default ProfileAvatarCard;